/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { TheWordGame } from "@packages/sdk";
import { URL_THE_WORD_IMAGE, useBranch } from "@packages/sdk";
import {
  constantSemanticColors,
  END_OF_BROWSER_ASYNC_QUEUE,
  ShareModal,
  spacing,
  Text,
  useModal,
  useThemeValues,
} from "@packages/ui";

const textStyle: Interpolation = {
  color: constantSemanticColors.onColorHighest,
  padding: spacing.m,
  textAlign: "center",
  whiteSpace: "pre-line",
};

export const useShareTheWord = () => {
  const { theWord } = useBranch();
  const modal = useModal(ShareModal);
  const theme = useThemeValues();
  const t = useTranslations();

  return async (game: TheWordGame) => {
    const link = await theWord({
      date: game?.available_at,
    });

    const GREEN_SQUARE = "\ud83d\udfe9";
    const YELLOW_SQUARE = "\ud83d\udfe8";
    const WHITE_SQUARE = "\u2b1c";

    const results = game?.summary?.matrix
      .map((results) =>
        results
          .map((res) =>
            res === "in_word_in_position"
              ? GREEN_SQUARE
              : res === "in_word"
                ? YELLOW_SQUARE
                : WHITE_SQUARE,
          )
          .join(""),
      )
      .join("\n");

    const text = t("the_word_share_text", {
      "0": game?.summary?.guesses?.length,
      "1": results,
    });

    setTimeout(() => {
      modal.show({
        color: theme.semanticColors.wordBackground,
        imgSrc: URL_THE_WORD_IMAGE,
        children: (
          <Text as="div" css={textStyle} type="title" size="l">
            {text}
          </Text>
        ),
        title: text,
        link,
      });
    }, END_OF_BROWSER_ASYNC_QUEUE);
  };
};
